import { useEffect, useRef, useState } from 'react'
import type { CommentHubComment } from 'shared-code/commenthub-api'
import { publicConfig } from 'config'
import {
  IDENTITIES_MAX_HASHES,
  UNRESOLVED_AUTHOR,
  type LiveIdentities,
  fetchLiveIdentities,
  liveReplyAuthor,
  subjectHash,
} from 'lib/community/identities'
import type { DiscussionIdentityOfThread } from 'lib/community/discussions-types'

type ReplyAuthor = { name: string; avatarUrl: string | null } | null

/**
 * The commentHub frame's `author` hook for a thread (review replies and discussion answers): the
 * thread's `identities` first; a reply hash missing from it is drawn as the neutral "Member" and
 * asked of DealHunt's bulk read (contract #213) — every hash the widget's render pass met, in ONE
 * call (a `setTimeout(0)` after the pass, ≤ 50 per call, the rest in the next), never twice on
 * the page. The answer merges into a local map and the widget redraws: an entry → DealHunt's
 * rule, an omitted hash → commentHub's own name. A failed read keeps "Member".
 *
 * The widget renders in the browser only; nothing here reaches the server HTML.
 */
export function useLiveReplyAuthor(
  identities: Record<string, DiscussionIdentityOfThread> | null | undefined
): (comment: CommentHubComment) => ReplyAuthor {
  const [live, setLive] = useState<LiveIdentities>({})
  // Hashes asked (or being asked) on this page, and the ones met since the last flush.
  const asked = useRef(new Set<string>())
  const pending = useRef(new Set<string>())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
    }
  }, [])

  const flush = (): void => {
    timer.current = null
    const batch = [...pending.current].slice(0, IDENTITIES_MAX_HASHES)
    batch.forEach(hash => {
      pending.current.delete(hash)
      asked.current.add(hash)
    })
    if (batch.length === 0) {
      return
    }
    void fetchLiveIdentities(batch, Boolean(publicConfig('useTrailingSlash'))).then(result => {
      if (result && alive.current) {
        setLive(prev => ({ ...prev, ...result }))
      }
      return null
    })
    if (pending.current.size > 0) {
      timer.current = setTimeout(flush, 0)
    }
  }

  return comment => {
    const author = liveReplyAuthor(comment.authorSubjectHash, identities, live)
    const hash = author === UNRESOLVED_AUTHOR ? subjectHash(comment.authorSubjectHash) : null
    if (hash && !asked.current.has(hash) && typeof window !== 'undefined') {
      pending.current.add(hash)
      if (!timer.current) {
        timer.current = setTimeout(flush, 0)
      }
    }
    return author
  }
}
